import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';
import {
  X,
  Search,
  ChevronRight,
  ChevronLeft,
  Rocket,
  LayoutDashboard,
  MessageCircle,
  Palette,
  Workflow,
  MessagesSquare,
  MessageSquare,
  HelpCircle,
} from 'lucide-react';
import { helpSections, HelpSection, HelpArticleConfig, getArticleConfigBySlug } from './content';
import ReactMarkdown from 'react-markdown';

// Declare window.tahc for TypeScript
declare global {
  interface Window {
    tahc?: {
      open: () => void;
      close: () => void;
      sendMessage: (msg: string) => void;
      reset: () => void;
      version: string;
    };
  }
}

interface HelpCenterProps {
  isOpen: boolean;
  onClose: () => void;
  initialArticle?: string; // slug to open directly
}

const iconMap: Record<string, React.ReactNode> = {
  rocket: <Rocket className="w-5 h-5" />,
  'layout-dashboard': <LayoutDashboard className="w-5 h-5" />,
  'message-circle': <MessageCircle className="w-5 h-5" />,
  palette: <Palette className="w-5 h-5" />,
  workflow: <Workflow className="w-5 h-5" />,
  'messages-square': <MessagesSquare className="w-5 h-5" />,
};

export default function HelpCenter({ isOpen, onClose, initialArticle }: HelpCenterProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<HelpArticleConfig | null>(null);
  const [searchResults, setSearchResults] = useState<HelpArticleConfig[]>([]);

  // Handle initial article
  useEffect(() => {
    if (initialArticle && isOpen) {
      const article = getArticleConfigBySlug(initialArticle);
      if (article) {
        setSelectedArticle(article);
      }
    }
  }, [initialArticle, isOpen]);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const allArticles = helpSections.flatMap(s => s.articles);
      const results = allArticles.filter(article => {
        const title = t(article.titleKey).toLowerCase();
        const description = t(article.descriptionKey).toLowerCase();
        const content = t(article.contentKey).toLowerCase();
        return title.includes(query) || description.includes(query) || content.includes(query);
      });
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, t]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedArticle(null);
      setSearchResults([]);
    }
  }, [isOpen]);

  // Load widget on-demand and open it
  const handleContactSupport = () => {
    onClose(); // Close help modal first

    const scriptId = 'tahc-support-widget';
    const existingScript = document.getElementById(scriptId);

    // Helper to wait for widget to be ready and then open it
    const waitAndOpen = (maxAttempts = 20) => {
      let attempts = 0;
      const check = () => {
        attempts++;
        if (window.tahc) {
          window.tahc.open();
        } else if (attempts < maxAttempts) {
          setTimeout(check, 100);
        }
      };
      check();
    };

    if (existingScript && window.tahc) {
      // Widget already loaded, just open it
      window.tahc.open();
    } else if (!existingScript) {
      // Load widget script on-demand with hidden launcher
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `${API_URL}/widget.js`;
      script.setAttribute('data-workspace-id', 'tahc-demo');
      script.setAttribute('data-start-hidden', 'true');
      script.async = true;
      script.onload = () => waitAndOpen();
      document.body.appendChild(script);
    } else {
      // Script exists but widget not ready yet
      waitAndOpen();
    }
  };

  if (!isOpen) return null;

  const handleBack = () => {
    setSelectedArticle(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleArticleClick = (article: HelpArticleConfig) => {
    setSelectedArticle(article);
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {selectedArticle && (
              <button
                onClick={handleBack}
                className="p-1 hover:bg-gray-100 rounded-lg transition"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <HelpCircle className="w-6 h-6 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedArticle ? t(selectedArticle.titleKey) : t('help.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search (only on home) */}
        {!selectedArticle && (
          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('help.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {selectedArticle ? (
            // Article View
            <ArticleView article={selectedArticle} onNavigate={handleArticleClick} />
          ) : searchQuery && searchResults.length > 0 ? (
            // Search Results
            <div className="space-y-2">
              <p className="text-sm text-gray-500 mb-3">
                {searchResults.length} {t('help.resultsFor')} "{searchQuery}"
              </p>
              {searchResults.map((article) => (
                <button
                  key={article.id}
                  onClick={() => handleArticleClick(article)}
                  className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition"
                >
                  <h3 className="font-medium text-gray-900">{t(article.titleKey)}</h3>
                  <p className="text-sm text-gray-600 mt-1">{t(article.descriptionKey)}</p>
                </button>
              ))}
            </div>
          ) : searchQuery ? (
            // No Results
            <div className="text-center py-8">
              <p className="text-gray-500">{t('help.noResults')} "{searchQuery}"</p>
              <p className="text-sm text-gray-400 mt-1">{t('help.tryDifferent')}</p>
            </div>
          ) : (
            // Section Grid
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {helpSections.map((section) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  onArticleClick={handleArticleClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">
              {t('help.cantFind')}
            </span>
            <button
              onClick={handleContactSupport}
              className="text-black hover:underline flex items-center gap-1"
            >
              {t('help.contactSupport')}
              <MessageSquare className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Section Card Component
function SectionCard({
  section,
  onArticleClick,
}: {
  section: HelpSection;
  onArticleClick: (article: HelpArticleConfig) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition">
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-gray-100 rounded-lg text-gray-700">
          {iconMap[section.icon] || <HelpCircle className="w-5 h-5" />}
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{t(section.titleKey)}</h3>
          <p className="text-xs text-gray-500">
            {section.articles.length} article{section.articles.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-3">{t(section.descriptionKey)}</p>
      <div className="space-y-1">
        {section.articles.map((article) => (
          <button
            key={article.id}
            onClick={() => onArticleClick(article)}
            className="w-full flex items-center justify-between p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition"
          >
            <span>{t(article.titleKey)}</span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>
        ))}
      </div>
    </div>
  );
}

// Article View Component
function ArticleView({
  article,
  onNavigate,
}: {
  article: HelpArticleConfig;
  onNavigate: (article: HelpArticleConfig) => void;
}) {
  const { t } = useTranslation();

  // Get FAQs from translation
  const getFaqs = () => {
    if (!article.faqKeys || article.faqKeys.length === 0) return [];

    const faqPrefix = article.faqKeys[0];
    const faqs: { question: string; answer: string }[] = [];

    // Try to get up to 10 FAQs
    for (let i = 1; i <= 10; i++) {
      const qKey = `${faqPrefix}.q${i}`;
      const aKey = `${faqPrefix}.a${i}`;
      const question = t(qKey);
      const answer = t(aKey);

      // Check if translation exists (doesn't return the key itself)
      if (question !== qKey && answer !== aKey) {
        faqs.push({ question, answer });
      } else {
        break;
      }
    }

    return faqs;
  };

  const faqs = getFaqs();

  // Get related articles
  const relatedArticles = article.relatedArticles
    ?.map((slug) => getArticleConfigBySlug(slug))
    .filter(Boolean) as HelpArticleConfig[] | undefined;

  return (
    <div className="space-y-6">
      {/* Main Content */}
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown
          components={{
            h1: ({ children }) => (
              <h1 className="text-2xl font-bold text-gray-900 mb-4">{children}</h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">{children}</h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">{children}</h3>
            ),
            p: ({ children }) => (
              <p className="text-gray-700 mb-3 leading-relaxed">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>
            ),
            li: ({ children }) => (
              <li className="text-gray-700">{children}</li>
            ),
            code: ({ children, className }) => {
              const isBlock = className?.includes('language-');
              if (isBlock) {
                return (
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm my-4">
                    <code>{children}</code>
                  </pre>
                );
              }
              return (
                <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm">
                  {children}
                </code>
              );
            },
            pre: ({ children }) => <>{children}</>,
            strong: ({ children }) => (
              <strong className="font-semibold text-gray-900">{children}</strong>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                className="text-blue-600 hover:underline"
                target={href?.startsWith('http') ? '_blank' : undefined}
                rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              >
                {children}
              </a>
            ),
          }}
        >
          {t(article.contentKey)}
        </ReactMarkdown>
      </div>

      {/* FAQs */}
      {faqs.length > 0 && (
        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('help.faqTitle')}
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-sm text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related Articles */}
      {relatedArticles && relatedArticles.length > 0 && (
        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('help.relatedArticles')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {relatedArticles.map((related) => (
              <button
                key={related.id}
                onClick={() => onNavigate(related)}
                className="text-left p-3 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition"
              >
                <h3 className="font-medium text-gray-900 text-sm">{t(related.titleKey)}</h3>
                <p className="text-xs text-gray-500 mt-1">{t(related.descriptionKey)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
